import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Bell, BellOff, Check, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { alarmsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { cn, formatDate, timeAgo } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SeverityBadge, StatusBadge } from '@/components/StatusBadge';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  PageShell,
  ScrollHint,
  apiMessage,
  fieldControl,
  glassPanel,
  glassSubtle,
  pillButton,
} from '@/pages/glass';

const matchesAlarm = (alarm, search) => {
  const haystack = [alarm.charge_point_id, alarm.error_code, alarm.message]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(search);
};

const AlarmsPage = () => {
  const { subscribe } = useWebSocket();
  const { can } = useAuth();
  const canManage = can('operations.manage');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [alarms, setAlarms] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [pendingClear, setPendingClear] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [ackingId, setAckingId] = useState(null);
  const loaded = useRef(false);

  const fetchAlarms = useCallback(async () => {
    try {
      const response = await alarmsApi.getAll({ limit: 200 });
      setAlarms(response.data || []);
      loaded.current = true;
      setLoadError('');
    } catch (error) {
      console.error('Failed to fetch alarms:', error);
      const message = apiMessage(error, 'Failed to load alarms');
      if (!loaded.current) setLoadError(message);
      else toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlarms();
    const unsubscribe = subscribe('alarms', (message) => {
      if (message.type === 'alarm_created' || message.type === 'alarm_acknowledged' || message.type === 'alarm_cleared') {
        fetchAlarms();
      }
    });
    return () => unsubscribe();
  }, [subscribe, fetchAlarms]);

  const filteredAlarms = alarms.filter((alarm) => {
    if (search && !matchesAlarm(alarm, search.trim().toLowerCase())) return false;
    if (statusFilter !== 'all' && alarm.status !== statusFilter) return false;
    if (severityFilter !== 'all' && alarm.severity !== severityFilter) return false;
    return true;
  });

  const activeCount = alarms.filter((alarm) => alarm.status === 'Active').length;
  const acknowledgedCount = alarms.filter((alarm) => alarm.status === 'Acknowledged').length;
  const criticalCount = alarms.filter((alarm) => alarm.severity === 'Critical' && alarm.status !== 'Cleared').length;
  const filtersActive = Boolean(search || statusFilter !== 'all' || severityFilter !== 'all');

  const handleAcknowledge = async (alarmId) => {
    setAckingId(alarmId);
    try {
      await alarmsApi.acknowledge(alarmId);
      toast.success('Alarm acknowledged');
      fetchAlarms();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to acknowledge alarm'));
    } finally {
      setAckingId(null);
    }
  };

  const handleClear = async () => {
    if (!pendingClear) return;
    setClearing(true);
    try {
      await alarmsApi.clear(pendingClear.id);
      toast.success('Alarm cleared');
      setPendingClear(null);
      fetchAlarms();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to clear alarm'));
    } finally {
      setClearing(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSeverityFilter('all');
  };

  const stats = [
    { label: 'Total', value: alarms.length, icon: AlertTriangle, tone: 'text-foreground', wash: 'bg-muted' },
    { label: 'Active', value: activeCount, icon: Bell, tone: 'text-red-700 dark:text-red-300', wash: 'bg-red-500/10', emphasize: activeCount > 0 },
    { label: 'Acknowledged', value: acknowledgedCount, icon: Check, tone: 'text-amber-800 dark:text-amber-200', wash: 'bg-amber-500/15' },
    { label: 'Critical open', value: criticalCount, icon: AlertTriangle, tone: 'text-red-700 dark:text-red-300', wash: 'bg-red-500/10', emphasize: criticalCount > 0 },
  ];

  return (
    <PageShell testId="alarms-page">
      <PageHeader
        eyebrow="Operations"
        title="Alarms"
        description="Review charger faults, acknowledge them, and clear them once they are resolved."
      />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
          <Card key={stat.label} className={cn(glassPanel, stat.emphasize && 'ring-red-500/40 dark:ring-red-400/40')}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className={cn('flex h-11 w-11 items-center justify-center rounded-2xl', stat.wash)}>
                <Icon className={cn('h-5 w-5', stat.tone)} aria-hidden="true" />
              </div>
              <div>
                <p className={cn('font-mono text-2xl font-semibold', stat.tone)}>{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      <Card className={glassPanel}>
        <CardContent className="grid gap-4 p-4 md:grid-cols-[1fr_11rem_11rem] md:items-end">
          <div className="space-y-2">
            <Label htmlFor="alarm-search">Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id="alarm-search"
                placeholder="Charger, error code, or message"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={cn(fieldControl, 'pl-10')}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="status-filter">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger id="status-filter" className={cn(fieldControl, 'w-full')} data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Acknowledged">Acknowledged</SelectItem>
                <SelectItem value="Cleared">Cleared</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="severity-filter">Severity</Label>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger id="severity-filter" className={cn(fieldControl, 'w-full')} data-testid="severity-filter">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="Major">Major</SelectItem>
                <SelectItem value="Minor">Minor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!canManage ? (
        <p className={cn(glassSubtle, 'px-4 py-2 text-sm text-muted-foreground')}>
          You can view alarms. Acknowledging and clearing them requires the operations.manage permission.
        </p>
      ) : null}

      <Card className={glassPanel}>
        <CardHeader>
          <CardTitle>Alarm history</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3" aria-busy="true" aria-live="polite">
              <span className="sr-only">Loading alarms</span>
              {[0, 1, 2, 3, 4].map((item) => (
                <div key={item} className={cn(glassSubtle, 'h-16 animate-pulse')} />
              ))}
            </div>
          ) : null}

          {!loading && loadError ? <ErrorState message={loadError} onRetry={fetchAlarms} /> : null}

          {!loading && !loadError && filteredAlarms.length === 0 ? (
            <EmptyState
              icon={BellOff}
              testId="no-alarms"
              title={alarms.length === 0 ? 'No alarms' : 'No alarms match these filters'}
              description={alarms.length === 0 ? 'Chargers have not reported any faults.' : 'Try a different search, status, or severity.'}
              action={filtersActive ? (
                <Button type="button" variant="outline" className={pillButton} onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            />
          ) : null}

          {!loading && !loadError && filteredAlarms.length > 0 ? (
            <>
              <ScrollHint />
              <Table className="min-w-[960px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Charger</TableHead>
                    <TableHead>Error code</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>First seen</TableHead>
                    <TableHead>Last seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAlarms.map((alarm) => (
                    <TableRow
                      key={alarm.id}
                      className={alarm.status === 'Active' ? 'bg-red-500/5 dark:bg-red-400/10' : undefined}
                      data-testid={`alarm-row-${alarm.id}`}
                    >
                      <TableCell>
                        <SeverityBadge severity={alarm.severity} />
                      </TableCell>
                      <TableCell className="font-mono">{alarm.charge_point_id}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {alarm.error_code}
                        {alarm.connector_id ? <span className="ml-1 text-xs">(C{alarm.connector_id})</span> : null}
                      </TableCell>
                      <TableCell className="max-w-xs truncate" title={alarm.message || undefined}>{alarm.message || '—'}</TableCell>
                      <TableCell>
                        <StatusBadge status={alarm.status} variant="dot" />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" title={formatDate(alarm.first_seen)}>
                        {timeAgo(alarm.first_seen)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" title={formatDate(alarm.last_seen)}>
                        {timeAgo(alarm.last_seen)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {alarm.status === 'Active' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={pillButton}
                              disabled={!canManage || ackingId === alarm.id}
                              onClick={() => handleAcknowledge(alarm.id)}
                              aria-label={`Acknowledge ${alarm.error_code || 'alarm'} on ${alarm.charge_point_id || 'charger'}`}
                              data-testid={`ack-alarm-${alarm.id}`}
                            >
                              <Check className="h-4 w-4" aria-hidden="true" />
                              {ackingId === alarm.id ? 'Saving…' : 'Ack'}
                            </Button>
                          ) : null}
                          {alarm.status !== 'Cleared' ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={pillButton}
                              disabled={!canManage}
                              onClick={() => setPendingClear(alarm)}
                              aria-label={`Clear alarm ${alarm.error_code || ''} on ${alarm.charge_point_id || 'charger'}`}
                              data-testid={`clear-alarm-${alarm.id}`}
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(pendingClear)}
        onOpenChange={(open) => { if (!open) setPendingClear(null); }}
        title="Clear this alarm?"
        description={pendingClear
          ? `${pendingClear.error_code || 'This fault'} on ${pendingClear.charge_point_id || 'the charger'} will leave the active list and stay in the history.`
          : ''}
        confirmLabel="Clear alarm"
        destructive
        pending={clearing}
        onConfirm={handleClear}
      />
    </PageShell>
  );
};

export default AlarmsPage;
